import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingOverlay } from '../elements/LoadingOverlay';
import { Pagination } from '../elements/Pagination';
import { StaffOnlyConfirmDeleteDialog } from '../elements/StaffOnlyConfirmDeleteDialog';
import { StaffOnlyDeleteCompleteDialog } from '../elements/StaffOnlyDeleteCompleteDialog';
import { StaffOnlyReservationList } from '../elements/StaffOnlyReservationList';
import { StaffOnlyResultsHeader } from '../elements/StaffOnlyResultsHeader';
import { StaffOnlySidebar } from '../elements/StaffOnlySidebar';
import { StaffLayout } from '../layouts/StaffLayout';
import type { API } from '@/api';
import { STAFF_SEARCH_PER_PAGE } from '@/constants/pagination';
import { CANCEL_CUTOFF_MINUTES } from '@/constants/reservation';
import { ROUTE_PATH } from '@/constants/routes';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useDialog } from '@/hooks/useDialog';

type StaffReservation =
  API['GET']['StaffReservationSearch']['response']['reservations'][number];

export interface StaffOnlySearchParams {
  space_type: string;
  airport?: string;
  flight_type?: string;
  terminal?: string;
  lounge?: string;
  search_date?: string;
  starts_at_hour?: string;
  starts_at_minute?: string;
  ends_at_hour?: string;
  ends_at_minute?: string;
  reservation_no?: string;
  name?: string;
}

export const StaffOnlyResults = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // 検索結果の状態管理
  const [reservations, setReservations] = useState<StaffReservation[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 削除結果の状態管理
  const [deleteResult, setDeleteResult] = useState<{
    succeededCount: number;
    failedCount: number;
    errorMessages: string[];
  }>({
    succeededCount: 0,
    failedCount: 0,
    errorMessages: [],
  });

  // ダイアログの制御
  const {
    dialogRef: confirmDialogRef,
    open: openConfirmDialog,
    close: closeConfirmDialog,
  } = useDialog();
  const {
    dialogRef: completeDialogRef,
    open: openCompleteDialog,
    close: closeCompleteDialog,
  } = useDialog();

  // ページネーション状態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // API リクエストフック
  const { isRequesting: isSearching, execute: executeSearch } = useApiRequest(
    'GET',
    'StaffReservationSearch',
    { showGlobalLoading: false },
  );
  const { isRequesting: isCanceling, execute: executeBulkCancel } =
    useApiRequest('POST', 'StaffBulkCancelReservations', {
      showGlobalLoading: false,
    });

  // location.stateから検索パラメータを取得
  const searchParams = location.state?.searchParams as
    | StaffOnlySearchParams
    | undefined;

  // 検索実行
  const fetchResults = useCallback(
    async (page: number = 1) => {
      if (!searchParams) return;

      const result = await executeSearch({
        ...searchParams,
        page,
        per_page: STAFF_SEARCH_PER_PAGE,
      });

      if (result.ok) {
        setReservations(result.data.reservations);
        setTotalCount(result.data.pagination.total_count);
        setCurrentPage(result.data.pagination.current_page);
      } else {
        console.error('Search error:', result.error);
        setSearchError(t('staffOnly.errors.searchFailed'));
      }
    },
    [searchParams, executeSearch, t],
  );

  // 検索パラメータがない場合は検索画面に戻る
  useEffect(() => {
    if (!searchParams) {
      navigate(ROUTE_PATH.STAFF_ONLY);
      return;
    }

    fetchResults(1);
  }, [searchParams, navigate, fetchResults]);

  // ページ変更ハンドラー
  const handlePageChange = (newPage: number) => {
    setSelectedIds(new Set()); // 選択をリセット
    fetchResults(newPage);
  };

  // 削除対象の選択トグル
  const handleToggleSelect = (reservationNo: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(reservationNo)) {
        next.delete(reservationNo);
      } else {
        next.add(reservationNo);
      }
      return next;
    });
  };

  // 実行ボタンクリック時（確認ダイアログを表示）
  const handleExecuteClick = () => {
    if (selectedIds.size === 0) {
      setSearchError(t('staffOnly.errors.noSelectionForDelete'));
      return;
    }
    openConfirmDialog();
  };

  // 確認ダイアログで削除を実行
  const handleConfirmDelete = async () => {
    closeConfirmDialog();

    // API呼び出し直前に再度キャンセル可否をチェック
    const now = new Date();
    const hasExpiredReservation = reservations.some((reservation) => {
      if (!selectedIds.has(reservation.reservation_no)) return false;
      const cutoffTime = new Date(
        new Date(reservation.starts_at).getTime() -
          CANCEL_CUTOFF_MINUTES * 60 * 1000,
      );
      return cutoffTime <= now;
    });

    // 1つでもキャンセル期限切れの予約があればエラー
    if (hasExpiredReservation) {
      setSearchError(t('staffOnly.errors.cancelCutoffPassed'));
      return;
    }

    const reservation_nos = Array.from(selectedIds);

    const result = await executeBulkCancel({
      reservation_nos,
    });

    if (result.ok) {
      const { succeeded_count, failed_count, errors } = result.data;

      // 失敗した予約番号のセットを作成
      const failedReservationNos = new Set(errors.map((e) => e.reservation_no));

      // 成功した予約のみをリストから削除
      setReservations((prev) =>
        prev.filter(
          (r) =>
            !selectedIds.has(r.reservation_no) ||
            failedReservationNos.has(r.reservation_no),
        ),
      );

      // 失敗した予約のみを選択状態に残す
      setSelectedIds((prev) => {
        const next = new Set<string>();
        prev.forEach((reservationNo) => {
          if (failedReservationNos.has(reservationNo)) {
            next.add(reservationNo);
          }
        });
        return next;
      });

      // 削除結果を保存
      const errorMessages = errors.map((e) => {
        const translatedError = t(`staffOnly.errors.${e.error}`, {
          defaultValue: e.error,
        });
        return `${e.reservation_no}: ${translatedError}`;
      });

      setDeleteResult({
        succeededCount: succeeded_count,
        failedCount: failed_count,
        errorMessages,
      });

      // 結果ダイアログを表示
      setSearchError(null);
      openCompleteDialog();
    } else {
      console.error('Bulk cancel error:', result.error);
      const errorKey = result.error || 'bulkCancelFailed';
      setSearchError(
        t(`staffOnly.errors.${errorKey}`, {
          defaultValue: t('staffOnly.errors.bulkCancelFailed'),
        }),
      );
    }
  };

  // 戻るボタンハンドラー（検索条件を持って戻る）
  const handleBack = () => {
    navigate(ROUTE_PATH.STAFF_ONLY, { state: { searchParams } });
  };

  const sidebar = (
    <StaffOnlySidebar
      mode="results"
      onExecute={handleExecuteClick}
      onBack={handleBack}
      selectedCount={selectedIds.size}
      selectedSpaceType={searchParams?.space_type}
      isLoading={isCanceling}
    />
  );

  return (
    <StaffLayout sidebar={sidebar}>
      <div className="e__line e__line--long">
        <div className="e__inner">
          <div className="e__area">
            {/* ローディング表示 - 検索中 */}
            {isSearching && (
              <LoadingOverlay message={t('staffOnly.messages.searching')} />
            )}

            {/* ローディング表示 - 削除中 */}
            {isCanceling && (
              <LoadingOverlay message={t('staffOnly.messages.canceling')} />
            )}

            {/* エラー表示（検索） */}
            {searchError && (
              <div className="e__alert e__alert--error">{searchError}</div>
            )}

            {/* 予約リスト */}
            {!isSearching && (
              <div className="e-reservation">
                {/* 件数表示 */}
                {reservations.length > 0 && (
                  <div className="e-reservation__count">
                    {t('staffOnly.results.description', { count: totalCount })}
                  </div>
                )}

                {/* ページネーション */}
                <div className="e-reservation__pagination">
                  <Pagination
                    totalCount={totalCount}
                    currentPage={currentPage}
                    perPage={STAFF_SEARCH_PER_PAGE}
                    onPageChange={handlePageChange}
                    disabled={isSearching}
                  />
                </div>

                {/* 予約リスト */}
                {reservations.length === 0 ? (
                  <div className="e-reservation__count">
                    {t('staffOnly.reservation.noResults')}
                  </div>
                ) : (
                  <div className="e-reservation__list">
                    <StaffOnlyResultsHeader />
                    <StaffOnlyReservationList
                      reservations={reservations}
                      selectedIds={selectedIds}
                      onToggleSelect={handleToggleSelect}
                      spaceType={searchParams?.space_type ?? ''}
                    />
                  </div>
                )}
              </div>
            )}

            {/* 削除確認ダイアログ */}
            <StaffOnlyConfirmDeleteDialog
              ref={confirmDialogRef}
              onClose={closeConfirmDialog}
              onConfirm={handleConfirmDelete}
              reservations={reservations}
              selectedIds={selectedIds}
              isDeleting={isCanceling}
            />

            {/* 削除完了ダイアログ */}
            <StaffOnlyDeleteCompleteDialog
              ref={completeDialogRef}
              onClose={() => {
                closeCompleteDialog();
                setSelectedIds(new Set());
                fetchResults(currentPage);
              }}
              succeededCount={deleteResult.succeededCount}
              failedCount={deleteResult.failedCount}
              errorMessages={deleteResult.errorMessages}
            />
          </div>
        </div>
      </div>
    </StaffLayout>
  );
};
